import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, Linking, ScrollView, Modal, Image } from 'react-native';
import { StyleSheet, Platform } from 'react-native';
import { colors } from '../styles/theme';
import { useNavigation } from '@react-navigation/native';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import { useTheme } from '../contexts/ThemeContext';
import { APP_VERSION } from '../config/environment';
import { useFixedFooter, useFooterResourcesVisible } from '../contexts/FeatureFlagsContext';
import { useExpenseTrackerInset, EXPENSE_TRACKER_ANIM_MS, EXPENSE_TRACKER_ANIM_EASING } from '../contexts/AppLayoutContext';
import { REVIEW_VRC_MOBILE_CLEARANCE } from './ReviewVrcButton';
import { RESOURCE_CATEGORIES, getResourceCategoryIconSize } from '../constants/resourceCategories';

const styles = StyleSheet.create({
  footerContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#E9E9E9',
    backdropFilter: 'none',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
    zIndex: 100,
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  footerContainerMobile: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  footer: {
    padding: 2,
    justifyContent: 'center',
    opacity: 0.6,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
      ':hover': {
        opacity: 1
      }
    }),
  },
  middleSection: {
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0.6,
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
      ':hover': {
        opacity: 1
      }
    }),
  },
  middleSectionText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textAlign: 'center',
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
    }),
  },
  footerText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
    }),
  },
  linkContainer: {
    ...(Platform.OS === 'web' && {
      display: 'inline',
      cursor: 'pointer',
    }),
  },
  designableText: {
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textDecorationLine: 'none',
    ...(Platform.OS === 'web' && {
      backgroundImage: 'linear-gradient(90deg, #4C1D95 0%, #9333EA 45%, #C4B5FD 100%)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      color: 'transparent',
      transition: 'opacity 0.3s ease',
    }),
    ...(Platform.OS !== 'web' && {
      color: '#9333EA',
    }),
  },
  disclaimerContainer: {
    textAlign: 'right',
  },
  disclaimerLink: {
    color: '#2ecc71',
    fontFamily: 'VastagoGrotesk',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 10,
      desktop: 10
    }),
    textDecorationLine: 'underline',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: 20,
  },
  modalContent: {
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    borderRadius: 10,
    padding: 20,
    maxWidth: 800,
    maxHeight: '80%',
    width: '90%',
  },
  modalTitle: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 24,
    color: 'white',
    marginBottom: 15,
    textAlign: 'center',
  },
  modalText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    color: 'white',
    marginBottom: 10,
    lineHeight: 20,
  },
  modalSection: {
    marginBottom: 20,
  },
  modalSectionTitle: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    fontSize: 18,
    color: 'white',
    marginBottom: 10,
    marginTop: 15,
  },
  closeButton: {
    alignSelf: 'center',
    marginTop: 20,
    backgroundColor: '#2ecc71',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
  },
  closeButtonText: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
    color: 'white',
    fontSize: 16,
  },
  emailLink: {
    color: '#2ecc71',
    textDecorationLine: 'underline',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  footerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    opacity: 0.6,
    position: 'relative',
    ...(Platform.OS === 'web' && {
      transition: 'opacity 0.3s ease',
      cursor: 'pointer',
      ':hover': {
        opacity: 1
      }
    }),
  },
  footerButtonText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 14,
      laptop: 14,
      desktop: 14
    }),
    fontWeight: '500',
    paddingHorizontal: 10,
  },
  tooltip: {
    position: 'absolute',
    bottom: '150%',
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    width: 200,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    opacity: 0,
    visibility: 'hidden',
    transition: 'all 0.3s ease',
    zIndex: 1000,
    pointerEvents: 'none',
  },
  tooltipVisible: {
    opacity: 1,
    visibility: 'visible',
  },
  tooltipText: {
    color: '#000000',
    fontFamily: 'Playpen Sans',
    fontSize: 12,
    lineHeight: 1.4,
    textAlign: 'center',
  },
  versionText: {
    fontFamily: 'Playpen Sans',
    fontWeight: '400',
    fontSize: getResponsiveValue({
      mobile: 8,
      tablet: 9,
      laptop: 9,
      desktop: 9,
    }),
    opacity: 0.35,
    marginTop: 3,
    textAlign: 'center',
  },
  leftSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  centerSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  centerNavLinks: {
    alignItems: 'center',
    gap: 0,
  },
  centerNavLinksMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: 8,
  },
  centerNavLinkTextMobile: {
    fontSize: 9,
    lineHeight: 11,
  },
  footerMobileCredit: {
    alignItems: 'center',
    width: '100%',
  },
  footerMobileStack: {
    alignItems: 'center',
    width: '100%',
    gap: 8,
  },
  centerNavLink: {
    opacity: 0.6,
    paddingVertical: 1,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'opacity 0.3s ease',
      ':hover': { opacity: 1 },
    }),
  },
  centerNavLinkText: {
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 11,
      tablet: 12,
      laptop: 12,
      desktop: 12,
    }),
    textTransform: 'uppercase',
    textAlign: 'center',
    lineHeight: getResponsiveValue({
      mobile: 14,
      tablet: 15,
      laptop: 15,
      desktop: 15,
    }),
  },
  centerMetaText: {
    fontFamily: 'Playpen Sans',
    fontWeight: '400',
    fontSize: getResponsiveValue({
      mobile: 7,
      tablet: 8,
      laptop: 8,
      desktop: 8,
    }),
    textAlign: 'center',
    opacity: 0.45,
    marginTop: 4,
    letterSpacing: 0.3,
  },
  centerMetaVersion: {
    fontFamily: 'Playpen Sans',
    fontWeight: '400',
    fontSize: getResponsiveValue({
      mobile: 6,
      tablet: 7,
      laptop: 7,
      desktop: 7,
    }),
    textAlign: 'center',
    opacity: 0.35,
    marginTop: 1,
  },
  rightSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  interestIconsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: getResponsiveValue({
      mobile: 12,
      tablet: 16,
      laptop: 20,
      desktop: 24,
    }),
  },
  interestIconsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: 96,
    rowGap: 4,
    columnGap: 6,
    justifyContent: 'space-between',
    alignSelf: 'center',
  },
  interestIconItem: {
    alignItems: 'center',
    gap: 4,
    maxWidth: 72,
  },
  interestIconSlot: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  interestIconItemGrid: {
    width: 44,
    alignItems: 'center',
    gap: 2,
  },
  interestIconLabel: {
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    fontSize: getResponsiveValue({
      mobile: 8,
      tablet: 9,
      laptop: 10,
      desktop: 10,
    }),
    textAlign: 'center',
  },
  interestIconLabelGrid: {
    fontSize: 6,
    lineHeight: 8,
  },
});

// For web platform, we need to create a hover-enabled component
const HoverableFooter = Platform.OS === 'web'
  ? ({ children }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    return (
      <View
        style={[
          styles.footer,
          isHovered && { opacity: 1 }
        ]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </View>
    );
  }
  : View;

const FOOTER_INTERESTS = RESOURCE_CATEGORIES;

function FooterInterestIcons({ isDarkMode, textColor, grid = false }) {
  const rowIconSize = getResponsiveValue({
    mobile: 36,
    tablet: 44,
    laptop: 48,
    desktop: 52,
  });
  const gridIconSize = 22;
  const maxRowIconSlot = Math.max(
    ...FOOTER_INTERESTS.map((item) => getResourceCategoryIconSize(item, rowIconSize)),
  );
  const maxGridIconSlot = Math.max(
    ...FOOTER_INTERESTS.map((item) => getResourceCategoryIconSize(item, gridIconSize)),
  );

  return (
    <View style={grid ? styles.interestIconsGrid : styles.interestIconsRow}>
      {FOOTER_INTERESTS.map((item) => {
        const baseSize = grid ? gridIconSize : rowIconSize;
        const iconSize = getResourceCategoryIconSize(item, baseSize);
        const slotSize = grid ? maxGridIconSlot : maxRowIconSlot;
        return (
        <View key={item.id} style={grid ? styles.interestIconItemGrid : styles.interestIconItem}>
          <View
            style={[
              styles.interestIconSlot,
              { width: slotSize, height: slotSize },
            ]}
          >
            <Image
              source={item.source}
              style={[
                { width: iconSize, height: iconSize },
                item.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                  ? { filter: 'invert(1)' }
                  : null,
              ]}
              resizeMode="contain"
            />
          </View>
          <Text
            style={[
              styles.interestIconLabel,
              grid && styles.interestIconLabelGrid,
              { color: textColor },
            ]}
            numberOfLines={1}
          >
            {item.label}
          </Text>
        </View>
      );})}
    </View>
  );
}

/** Privacy and Terms. Desktop shows them in the footer's left column so the center stack stays short. */
function FooterLegalLinks({ navigation, textStyle, linkStyle, row = false }) {
  return (
    <>
      <TouchableOpacity style={linkStyle} onPress={() => navigation.navigate('Privacy')} accessibilityRole="link">
        <Text style={textStyle}>Privacy</Text>
      </TouchableOpacity>
      {row ? <View style={{ width: 18 }} /> : null}
      <TouchableOpacity style={linkStyle} onPress={() => navigation.navigate('Terms')} accessibilityRole="link">
        <Text style={textStyle}>Terms</Text>
      </TouchableOpacity>
    </>
  );
}

function FooterCenterNav({
  themedStyles,
  navigation,
  isDonateHovered,
  setIsDonateHovered,
  linkStyle,
  currentYear,
  appVersion,
  isMobileLayout = false,
  showLegal = true,
}) {
  const linkTextStyle = [
    styles.centerNavLinkText,
    isMobileLayout && styles.centerNavLinkTextMobile,
    themedStyles.buttonText,
  ];

  return (
    <View style={[styles.centerNavLinks, isMobileLayout && styles.centerNavLinksMobile]}>
      <TouchableOpacity
        style={[styles.centerNavLink, linkStyle]}
        onPress={() => navigation.navigate('AboutUs')}
      >
        <Text style={linkTextStyle}>About</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.centerNavLink, linkStyle]}
        onPress={() => navigation.navigate('Contact')}
      >
        <Text style={linkTextStyle}>Contact</Text>
      </TouchableOpacity>

      {showLegal ? (
        <FooterLegalLinks navigation={navigation} textStyle={linkTextStyle} linkStyle={[styles.centerNavLink, linkStyle]} />
      ) : null}

      <TouchableOpacity
        style={[styles.centerNavLink, linkStyle]}
        onPress={() => Linking.openURL('https://buymeacoffee.com/venuereportcard')}
        onMouseEnter={() => setIsDonateHovered(true)}
        onMouseLeave={() => setIsDonateHovered(false)}
      >
        <Text style={linkTextStyle}>Donate</Text>
        {!isMobileLayout && Platform.OS === 'web' && (
          <View style={[styles.tooltip, isDonateHovered && styles.tooltipVisible]}>
            <Text style={styles.tooltipText}>
              Help us make Venue Report Card even better! Your support enables us to add new features, improve data accuracy, and create a more comprehensive resource for the live events community. Every contribution makes a difference!
            </Text>
          </View>
        )}
      </TouchableOpacity>

      {!isMobileLayout && (
        <>
          <Text style={[styles.centerMetaText, { color: themedStyles.text.color }]}>
            VENUE REPORT CARD LLC {currentYear}
          </Text>
          {appVersion ? (
            <Text style={[styles.centerMetaVersion, { color: themedStyles.text.color }]}>
              v{appVersion}
            </Text>
          ) : null}
        </>
      )}
    </View>
  );
}

function FooterCenterMeta({ themedStyles, currentYear, appVersion, centered = false }) {
  return (
    <View style={{ alignItems: centered ? 'center' : 'flex-start' }}>
      <Text style={[styles.centerMetaText, { color: themedStyles.text.color, marginTop: 0, textAlign: centered ? 'center' : 'left' }]}>
        VENUE REPORT CARD LLC {currentYear}
      </Text>
      {appVersion ? (
        <Text style={[styles.centerMetaVersion, { color: themedStyles.text.color, textAlign: centered ? 'center' : 'left' }]}>
          v{appVersion}
        </Text>
      ) : null}
    </View>
  );
}

export function Footer({ isAdminPage = false, backgroundColor, pinned }) {
  const currentYear = new Date().getFullYear();
  const FooterComponent = HoverableFooter;
  const [disclaimerVisible, setDisclaimerVisible] = useState(false);
  const [isDonateHovered, setIsDonateHovered] = useState(false);
  const navigation = useNavigation();
  const [isMobile, setIsMobile] = useState(false);
  const { theme, isDarkMode } = useTheme();
  const fixedFooterEnabled = useFixedFooter();
  const footerResourcesVisible = useFooterResourcesVisible();
  const expenseInset = useExpenseTrackerInset();
  const isPinned = !isMobile && (
    pinned === false ? false : (pinned === true || (pinned == null && fixedFooterEnabled))
  );

  // Themed colors
  const themedStyles = useMemo(() => ({
    container: {
      backgroundColor: isDarkMode ? theme.background.secondary : '#E9E9E9',
      borderTopColor: isDarkMode ? theme.ui.border : 'rgba(0, 0, 0, 0.1)',
    },
    text: {
      color: isDarkMode ? theme.text.secondary : '#000000',
    },
    buttonText: {
      color: isDarkMode ? theme.text.primary : '#000000',
    },
  }), [isDarkMode, theme]);

  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        setIsMobile(width <= BREAKPOINTS.mobile);
      }
    };
    
    checkScreenSize();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, []);


  if (isAdminPage) {
    return null;
  }


  return (
    <View style={[
      styles.footerContainer,
      isMobile && styles.footerContainerMobile,
      // REQ-070: leave room under the phone footer for the fixed Review VRC button.
      isMobile && Platform.OS === 'web' ? { paddingBottom: 10 + REVIEW_VRC_MOBILE_CLEARANCE } : null,
      themedStyles.container,
      backgroundColor ? { backgroundColor, borderTop: 'none' } : null,
      isPinned && Platform.OS === 'web' ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: expenseInset,
        zIndex: 900,
        width: 'auto',
        transition: `right ${EXPENSE_TRACKER_ANIM_MS}ms ${EXPENSE_TRACKER_ANIM_EASING}`,
      } : {
        position: 'relative',
        bottom: 'auto',
        zIndex: 1,
      },
    ]}>
      {isMobile ? (
        <View style={styles.footerMobileStack}>
          <FooterCenterNav
            themedStyles={themedStyles}
            navigation={navigation}
            isDonateHovered={isDonateHovered}
            setIsDonateHovered={setIsDonateHovered}
            currentYear={currentYear}
            appVersion={APP_VERSION}
            isMobileLayout
          />

          {footerResourcesVisible ? (
            <FooterInterestIcons isDarkMode={isDarkMode} textColor={themedStyles.text.color} grid />
          ) : null}

          <FooterCenterMeta
            themedStyles={themedStyles}
            currentYear={currentYear}
            appVersion={APP_VERSION}
            centered
          />

          <View style={[styles.footerMobileCredit, { alignItems: 'flex-end' }]}>
            <FooterComponent>
              <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Text style={[styles.footerText, themedStyles.text, { fontSize: 8 }]}>CREATED WITH LOVE BY TEAM </Text>
                <TouchableOpacity
                  onPress={() => Linking.openURL('https://www.dsignable.com')}
                  style={styles.linkContainer}
                >
                  <Text style={[styles.designableText, { fontSize: 8 }]}>DSIGNABLE</Text>
                </TouchableOpacity>
              </View>
            </FooterComponent>
          </View>
        </View>
      ) : (
        <>
          <View style={styles.leftSection}>
            {footerResourcesVisible ? (
              <FooterInterestIcons isDarkMode={isDarkMode} textColor={themedStyles.text.color} />
            ) : null}
            {/* Trev 2026-10-28: Privacy / Terms sit here, side by side, so the footer isn't so tall. */}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: footerResourcesVisible ? 8 : 0 }}>
              <FooterLegalLinks
                row
                navigation={navigation}
                textStyle={[styles.centerNavLinkText, themedStyles.buttonText]}
                linkStyle={styles.centerNavLink}
              />
            </View>
          </View>

          <View style={styles.centerSection}>
            <FooterCenterNav
              themedStyles={themedStyles}
              navigation={navigation}
              isDonateHovered={isDonateHovered}
              setIsDonateHovered={setIsDonateHovered}
              linkStyle={{ position: 'relative' }}
              currentYear={currentYear}
              appVersion={APP_VERSION}
              showLegal={false}
            />
          </View>

          <View style={styles.rightSection}>
            <FooterComponent>
              <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Text style={[styles.footerText, themedStyles.text]}>CREATED WITH LOVE BY TEAM </Text>
                <TouchableOpacity
                  onPress={() => Linking.openURL('https://www.dsignable.com')}
                  style={styles.linkContainer}
                >
                  <Text style={styles.designableText}>DSIGNABLE</Text>
                </TouchableOpacity>
              </View>
            </FooterComponent>
          </View>
        </>
      )}

      {/* <View style={[styles.footer, styles.disclaimerContainer]}>
        <TouchableOpacity onPress={() => setDisclaimerVisible(true)}>
          <Text style={styles.disclaimerLink}>DISCLAIMER & TERMS</Text>
        </TouchableOpacity>
      </View> */}

      <Modal
        visible={disclaimerVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setDisclaimerVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <ScrollView showsVerticalScrollIndicator={true}>
              <Text style={styles.modalTitle}>DISCLAIMER & TERMS</Text>
              
              <View style={styles.modalSection}>
                <Text style={styles.modalText}>We are not associated with any venue. The info compiled here was taken directly from venue tech sheets and websites.</Text>
                <Text style={styles.modalText}>All info collected here is subject to change without notice. You should always advance with the venue directly to receive the most up-to-date info.</Text>
                <Text style={styles.modalText}>We know that's not easy, so we created this database. We tried to make it as simple as possible.</Text>
                <Text style={styles.modalText}>Key components here are: consoles, speakers, backline, venue parking.</Text>
              </View>

              <View style={styles.modalSection}>
                <Text style={styles.modalText}><Text style={{fontWeight: '700'}}>PLEASE NOTE:</Text> Backline only covers a drum set as minimum. It has nothing to do with mics or stands.</Text>
                <Text style={styles.modalText}>If you've toured through and know some stuff, please fill it in the spaces open below. When enough people start sending us the same basic info, we'll update. Be sure to hit that submit button!</Text>
              </View>

              <View style={styles.modalSection}>
                <Text style={styles.modalText}>If you think we have incorrect info, let us know at <Text style={styles.emailLink} onPress={() => Linking.openURL('mailto:contact@venuereportcard.com?subject=fix it')}>contact@venuereportcard.com</Text> with subject line: fix it</Text>
                <Text style={styles.modalText}>If you have a useful link with more venue data, please email us at <Text style={styles.emailLink} onPress={() => Linking.openURL('mailto:contact@venuereportcard.com?subject=spec link')}>contact@venuereportcard.com</Text> with subject line: spec link</Text>
                <Text style={styles.modalText}>If you have updated tech info to share about a venue you either own or work at, please email us at <Text style={styles.emailLink} onPress={() => Linking.openURL('mailto:contact@venuereportcard.com?subject=VENUE')}>contact@venuereportcard.com</Text> with subject line: VENUE</Text>
                <Text style={styles.modalText}>We will never share booking or management emails.</Text>
                <Text style={styles.modalText}>If there is a venue you know and love that's missing, please click the + VENUE link at the top right of the form below. It will bring you to a new blank form to fill out and includes a dedicated space for a venue tech spec link to share.</Text>
                <Text style={styles.modalText}>Thank you for your participation. We hope this helps.</Text>
              </View>

              <TouchableOpacity 
                style={styles.closeButton}
                onPress={() => setDisclaimerVisible(false)}
              >
                <Text style={styles.closeButtonText}>Close</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
} 